<script setup lang="ts">
import { useForm } from 'vee-validate'
import { object, string } from 'yup'

const { open, vacancy } = defineProps<{
  /**
   * Whether the modal is open.
   */
  open?: boolean
  /**
   * The vacancy to apply for.
   */
  vacancy: Vacancy
}>()

const emit = defineEmits(['close'])
const { t: $t } = useI18n({ useScope: 'global' })
const { t } = useI18n({ useScope: 'local' })

const { isAuthenticated } = useAuth()
const { currentUser } = storeToRefs(useCurrentUserStore())

const config = useRuntimeConfig()
const isMeedoen = config.public.isMeedoen
const emailRequired = !isMeedoen
const phoneNumberRequired = isMeedoen

const schema = isAuthenticated.value
  ? object({
    message: string().label($t('application.attributes.message')),
  })
  : object({
    firstName: string()
      .required()
      .label($t('application.attributes.firstName')),
    lastName: string()
      .required()
      .label($t('application.attributes.lastName')),
    email: emailRequired
      ? string().email().required().label($t('application.attributes.email'))
      : string().optional(),
    phoneNumber: phoneNumberRequired
      ? string().label($t('application.attributes.phoneNumber')).required()
      : string().label($t('application.attributes.phoneNumber')),
    message: string().label($t('application.attributes.message')),
  })

// const initialValues = {}
// const initialValues = process.dev
//   ? {
//       firstName: 'Gertjan',
//       lastName: 'Jansen',
//       email: 'gertjan@miniem.nl',
//     }
//   : {}

const { handleSubmit, isSubmitting, resetForm, setFieldError } = useForm({
  validationSchema: toTypedSchema(schema),
  // initialValues,
})

const submittedApplication = ref<ActivityApplication | undefined>(undefined)
const success = ref(false)

const { handleServerError } = useServerErrorHandler(schema, setFieldError)
const { handleValidationError } = useFormValidationErrorHandler()

const submit = handleSubmit(async (values) => {
  try {
    const application = await createApplication({
      vacancyId: vacancy.id,
      ...values,
    })
    submittedApplication.value = application
    success.value = true
    resetForm()
  }
  catch (error) {
    handleServerError(error)
  }
}, handleValidationError)
</script>

<template>
  <BaseModal :open="open" @close="emit('close')">
    
    <template #title>
      {{ t('headingTitle') }} {{ vacancy.title }}
    </template>
    <template v-if="!success">
      <BaseParagraph>{{ t('headingText') }}</BaseParagraph>
      <form
        method="POST"
        novalidate
        @submit.prevent="submit"
      >
        <div class="mt-2 grid grid-cols-12 gap-4">
          <template v-if="!isAuthenticated">
            <div v-if="emailRequired" class="col-span-12">
              <Field v-slot="{ field, errorMessage }" name="email">
                <BaseInput
                  icon="lucide:mail"
                  v-bind="field"
                  :error="errorMessage"
                  :label="$t('application.attributes.email')"
                  :placeholder="$t('application.placeholders.email')"
                  type="email"
                  autocomplete="email"
                />
              </Field>
            </div>

            <div class="col-span-6">
              <Field v-slot="{ field, errorMessage }" name="firstName">
                <BaseInput
                  icon="lucide:user"
                  v-bind="field"
                  :error="errorMessage"
                  :label="$t('application.attributes.firstName')"
                  :placeholder="$t('application.placeholders.firstName')"
                />
              </Field>
            </div>

            <div class="col-span-6">
              <Field v-slot="{ field, errorMessage }" name="lastName">
                <BaseInput
                  icon="lucide:user"
                  v-bind="field"
                  :error="errorMessage"
                  :label="$t('application.attributes.lastName')"
                  :placeholder="$t('application.placeholders.lastName')"
                />
              </Field>
            </div>

            <div class="col-span-6">
              <Field v-slot="{ field, errorMessage }" name="phoneNumber">
                <BaseInput
                  icon="lucide:smartphone"
                  v-bind="field"
                  :error="errorMessage"
                  :label="$t('application.attributes.phoneNumber')"
                  :placeholder="$t('application.placeholders.phoneNumber')"
                />
              </Field>
            </div>
          </template>

          <FormTextarea
            name="message"
            :label="$t('application.attributes.message')"
            :placeholder="$t('application.placeholders.message')"
            :rows="4"
          />
        </div>

        
        <BaseParagraph v-if="isAuthenticated" class="mt-4">
          {{ t('privacyMessage') }}
        </BaseParagraph>

        <ul class="mt-2">
          <li v-if="currentUser?.email">
            <Icon name="ph:envelope" />
            {{ currentUser.email }}
          </li>
          <li v-if="currentUser?.phoneNumber">
            <Icon name="ph:phone" />
            {{ currentUser.phoneNumber }}
          </li>
        </ul>
      </form>
    </template>

    <VacancyDetailsApplySuccess
      v-else-if="submittedApplication"
      :vacancy="vacancy"
      :application="submittedApplication"
    />

    <template #buttons>
      <BaseButton @click="$emit('close')">
        {{
          submittedApplication ? $t('close') : $t('cancel')
        }}
      </BaseButton>

      <SubmitButton
        v-if="!submittedApplication"
        :is-submitting="isSubmitting"
        @click="submit"
      >
        {{ $t('apply') }}
      </SubmitButton>
    </template>
  </BaseModal>
</template>

<i18n lang="json">
{
  "en": {
    "headingTitle": "Apply for",
    "headingText": "Register for this vacancy by filling in your details.",
    "privacyMessage": "The following contact details will be sent to the organization:"
  },
  "nl": {
    "headingTitle": "Aanmelden voor",
    "headingText": "Meld je aan voor deze vacature door je gegevens in te vullen.",
    "privacyMessage": "De volgende contactgegevens worden meegestuurd naar de organisatie:"
  }
}
</i18n>
